import { Fragment } from 'react'

import { Box, Grid } from '@sequencehq/core-components'

import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { ConditionPropertyEditorComponent } from 'Automations/nodeDefinitions/conditions'
import type { QuoteComparisonSubOperator } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { filterErrorsByField } from 'Automations/automationEditor/utils'
import { QuoteTemplateSelector } from '../../../../switch/components/GroupEditor/inputs'
import {
  comparisonOptionsFor,
  isQuoteComparisonField,
  parseExpression
} from '../quoteComparison'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import {
  readRow,
  useQuoteTemplateOperatorState
} from './QuoteTemplatePropertyEditor.helpers'

/**
 * Property-level editor for QUOTE_TEMPLATE. Owns the template picker, the
 * template-field dropdown, and the sub-row the field offers — which waits on
 * both picks above it, since its labels agree with the chosen field.
 *
 *   +----------------+----------------+
 *   | template       | field          |   field: "All prices"
 *   +----------------+----------------+
 *   | is / is not                     |
 *   +---------------------------------+
 *
 * Pricing's comparisons sit in the field dropdown beside the other fields, so
 * one pick names what is compared. The row still stores them apart.
 *
 * What the row effectively holds — the defaults, and the fallback when a sibling
 * has taken a comparison — is decided and committed by
 * `useQuoteTemplateOperatorState`. This component renders those values and
 * writes the user's picks.
 */
export const QuoteTemplatePropertyEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  onChange,
  operators,
  siblingConditions,
  validationErrors,
  focusOnInitialLoad
}) => {
  const row = readRow(value)

  const {
    expressionOptions,
    expressionDisabled,
    expression,
    subOperator,
    exhaustedTemplateIds
  } = useQuoteTemplateOperatorState({
    operator,
    row,
    operators,
    siblingConditions,
    onChange
  })

  // Each dropdown writes the whole value, so it carries the others through.
  const update = (next: {
    templateId?: string
    subOperator?: QuoteComparisonSubOperator
  }) =>
    onChange({
      value: {
        templateId: next.templateId ?? row.templateId ?? '',
        operator: next.subOperator ?? subOperator,
        comparison: row.comparison
      }
    })

  // One pick carries both halves: the field the row compares on, and — for
  // pricing — which comparison. What is stored keeps them apart.
  const onFieldChange = (selected: unknown) => {
    const { field, comparison } = parseExpression(String(selected))
    onChange({
      operator: field,
      value: {
        templateId: row.templateId ?? '',
        operator: subOperator,
        comparison
      }
    })
  }

  const templateSelected = Boolean(row.templateId)
  const operatorSelected = isQuoteComparisonField(operator)

  return (
    <Fragment>
      <Grid
        gridTemplateColumns="1fr 1fr"
        gap="8px"
        gridTemplateAreas='"template operator" "subOperator subOperator"'
      >
        <Box gridArea="template">
          <QuoteTemplateSelector
            data-testid={`${dataTestId}.template`}
            value={row.templateId ?? ''}
            onChange={selected => update({ templateId: selected })}
            excludeIds={exhaustedTemplateIds}
            validationErrors={filterErrorsByField(
              validationErrors?.value,
              'templateId'
            )}
            initiallyOpen={focusOnInitialLoad}
          />
        </Box>
        <Box gridArea="operator">
          <InputSelectControlField
            data-testid={`${dataTestId}.operator`}
            placeholder="Select field..."
            options={expressionOptions}
            value={expression}
            disabled={expressionDisabled}
            onChange={onFieldChange}
            validationErrors={validationErrors?.operator}
          />
        </Box>
        {templateSelected && operatorSelected && (
          <Box gridArea="subOperator" width={SUB_OPERATOR_COLUMN_WIDTH}>
            <InputSelectControlField
              data-testid={`${dataTestId}.subOperator`}
              value={subOperator}
              onChange={selected =>
                update({
                  subOperator: selected as QuoteComparisonSubOperator
                })
              }
              options={comparisonOptionsFor({ operator, value })}
              placeholder="Select..."
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'operator'
              )}
            />
          </Box>
        )}
      </Grid>
    </Fragment>
  )
}
